import { useCallback, useRef, useMemo } from 'react';
import { useMeetingSelector } from '../meeting/meetingProviderContextDef';
import {
  useParticipantTrackedState,
  PARTICIPANT_STATE_KEYS,
  EMPTY_PARTICIPANT_STATE
} from './participantStateContext';
import { useStableEvent } from '../meeting/meetingProviderContextDef';
import { events } from '../utils';

const NOOP = () => {};

/**
 *
 * @param {string} participantId
 * @param {{
 *  onStreamDisabled?: Function,
 *  onStreamEnabled?: Function,
 *  onMediaStatusChanged?: Function,
 *  onVideoQualityChanged?: Function,
 *  onStreamPaused?: Function,
 *  onStreamResumed?: Function,
 *  onProducerAdded?: Function,
 *  onProducerRemoved?: Function,
 *  onConsumerAdded?: Function,
 *   onConsumerRemoved?: Function
 * }} options
 *
 * @returns {{
 *   displayName: string;
 *   participant: Participant;
 *   webcamStream: Stream;
 *   micStream: Stream;
 *   screenShareStream: Stream;
 *   screenShareAudioStream: Stream;
 *   webcamOn: boolean;
 *   micOn: boolean;
 *   screenShareOn: boolean;
 *   screenShareAudioOn: boolean;
 *   isLocal: boolean;
 *   isActiveSpeaker: boolean;
 *   isMainParticipant: boolean;
 *   pinState: any;
 *   mode: "SEND_AND_RECV" | "SIGNALLING_ONLY" | "RECV_ONLY";
 *   metaData: object;
 *   consumeMicStreams: () => void;
 *   consumeWebcamStreams: () => void;
 *   stopConsumingMicStreams: () => void;
 *   stopConsumingWebcamStreams: () => void;
 *   setQuality: (quality: "low" | "med" | 'high') => void;
 *   setScreenShareQuality: (quality: "low" | "med" | 'high') => void;
 *   setViewPort: (width: number, height: number) => void;
 *   enableMic: () => void;
 *   disableMic: () => void;
 *   enableWebcam: () => void;
 *   disableWebcam: () => void;
 *   remove: () => void;
 *   captureImage: ({height,width}) => Promise<string | null>;
 *   pin: (data: "SHARE_AND_CAM" | "CAM" | "SHARE") => void;
 *   unpin: (data: "SHARE_AND_CAM" | "CAM" | "SHARE") => void;
 *   getShareAudioStats: () => Promise<[{
 *   bitrate: number,
 *   rtt: number,
 *   network:String,
 *   codec: String,
 *   jitter: number,
 *   limitation: Object,
 *   totalPackets: number,
 *   packetsLost: number,
 *   concealmentEvents: number,
 *   insertedSamplesForDecelaration: number,
 *   removedSamplesForAccelaration: number,
 *   size:Object
 * }]>;
 *   getAudioStats: () => Promise<[{
 *   bitrate: number,
 *   rtt: number,
 *   network:String,
 *   codec: String,
 *   jitter: number,
 *   limitation: Object,
 *   totalPackets: number,
 *   packetsLost: number,
 *   concealmentEvents: number,
 *   insertedSamplesForDecelaration: number,
 *   removedSamplesForAccelaration: number,
 *   size:Object
 * }]>;
 * getVideoStats: () => Promise<[{
 *   bitrate: number,
 *   rtt: number,
 *   network:String,
 *   codec: String,
 *   jitter: number,
 *   limitation: Object,
 *   totalPackets: number,
 *   packetsLost: number,
 *   concealmentEvents: number,
 *   insertedSamplesForDecelaration: number,
 *   removedSamplesForAccelaration: number,
 *   size:Object,
 *   currentSpatialLayer: number,
 *   currentTemporalLayer: number,
 *   preferredSpatialLayer: number,
 *   preferredTemporalLayer: number
 * }]>;
 * getShareStats: () => Promise<[{
 *   bitrate: number,
 *   rtt: number,
 *   network:String,
 *   codec: String,
 *   jitter: number,
 *   limitation: Object,
 *   totalPackets: number,
 *   packetsLost: number,
 *   concealmentEvents: number,
 *   insertedSamplesForDecelaration: number,
 *   removedSamplesForAccelaration: number,
 *   size:Object,
 *   currentSpatialLayer: number,
 *   currentTemporalLayer: number,
 *   preferredSpatialLayer: number,
 *   preferredTemporalLayer: number
 * }]>;
 *}}
 *
 */
const useParticipant = (
  participantId,
  {
    onStreamEnabled = NOOP,
    onStreamDisabled = NOOP,
    onMediaStatusChanged = NOOP,
    onVideoQualityChanged = NOOP,
    onStreamPaused = NOOP,
    onStreamResumed = NOOP,
    onProducerAdded = NOOP,
    onProducerRemoved = NOOP,
    onConsumerAdded = NOOP,
    onConsumerRemoved = NOOP
  } = {}
) => {
  const allState = useParticipantTrackedState();

  const selectParticipant = useCallback(
    s => s.participants?.get(participantId),
    [participantId]
  );
  const participant = useMeetingSelector(selectParticipant);
  const participantRef = useRef(participant);
  participantRef.current = participant;

  if (participant?.isAgent) {
    throw new Error(
      `useParticipant: "${participantId}" is an agent participant. Use useAgentParticipant() instead.`
    );
  }

  useStableEvent(
    `${events['participant-stream-enabled']}-${participantId}`,
    onStreamEnabled
  );
  useStableEvent(
    `${events['participant-stream-disabled']}-${participantId}`,
    onStreamDisabled
  );
  useStableEvent(
    `${events['participant-media-status-changed']}-${participantId}`,
    onMediaStatusChanged
  );
  useStableEvent(
    `${events['participant-video-quality-changed']}-${participantId}`,
    onVideoQualityChanged
  );
  useStableEvent(
    `${events['participant-stream-paused']}-${participantId}`,
    onStreamPaused
  );
  useStableEvent(
    `${events['participant-stream-resumed']}-${participantId}`,
    onStreamResumed
  );
  useStableEvent(
    `${events['participant-producer-added']}-${participantId}`,
    onProducerAdded
  );
  useStableEvent(
    `${events['participant-producer-removed']}-${participantId}`,
    onProducerRemoved
  );
  useStableEvent(
    `${events['participant-consumer-added']}-${participantId}`,
    onConsumerAdded
  );
  useStableEvent(
    `${events['participant-consumer-removed']}-${participantId}`,
    onConsumerRemoved
  );

  const setQuality = useCallback(
    q => participantRef.current?.setQuality(q),
    []
  );
  const setViewPort = useCallback(
    (w, h) => participantRef.current?.setViewPort(w, h),
    []
  );
  const setScreenShareQuality = useCallback(
    q => participantRef.current?.setScreenShareQuality(q),
    []
  );
  const enableMic = useCallback(() => participantRef.current?.enableMic(), []);
  const disableMic = useCallback(
    () => participantRef.current?.disableMic(),
    []
  );
  const enableWebcam = useCallback(
    () => participantRef.current?.enableWebcam(),
    []
  );
  const disableWebcam = useCallback(
    () => participantRef.current?.disableWebcam(),
    []
  );
  const pin = useCallback(d => participantRef.current?.pin(d), []);
  const unpin = useCallback(d => participantRef.current?.unpin(d), []);
  const remove = useCallback(() => participantRef.current?.remove(), []);
  const captureImage = useCallback(
    async ({ height, width } = {}) =>
      participantRef.current?.captureImage({ height, width }),
    []
  );
  const getAudioStats = useCallback(
    async () => participantRef.current?.getAudioStats(),
    []
  );
  const getVideoStats = useCallback(
    async () => participantRef.current?.getVideoStats(),
    []
  );
  const getTransportStats = useCallback(
    async () => participantRef.current?.getTransportStats(),
    []
  );
  const getShareStats = useCallback(
    async () => participantRef.current?.getShareStats(),
    []
  );
  const getShareAudioStats = useCallback(
    async () => participantRef.current?.getShareAudioStats(),
    []
  );
  const consumeWebcamStreams = useCallback(
    () => participantRef.current?.consumeWebcamStreams(),
    []
  );
  const consumeMicStreams = useCallback(
    () => participantRef.current?.consumeMicStreams(),
    []
  );
  const stopConsumingWebcamStreams = useCallback(
    () => participantRef.current?.stopConsumingWebcamStreams(),
    []
  );
  const stopConsumingMicStreams = useCallback(
    () => participantRef.current?.stopConsumingMicStreams(),
    []
  );

  const methodsRef = useRef(null);
  methodsRef.current = {
    consumeMicStreams,
    consumeWebcamStreams,
    stopConsumingMicStreams,
    stopConsumingWebcamStreams,
    setQuality,
    setScreenShareQuality,
    setViewPort,
    enableMic,
    disableMic,
    enableWebcam,
    disableWebcam,
    captureImage,
    pin,
    unpin,
    remove,
    getAudioStats,
    getVideoStats,
    getShareStats,
    getShareAudioStats,
    getTransportStats
  };

  return useMemo(
    () =>
      new Proxy(allState, {
        get(target, prop, receiver) {
          if (prop in PARTICIPANT_STATE_KEYS) {
            const pState = target[participantId];
            return pState ? pState[prop] : EMPTY_PARTICIPANT_STATE[prop];
          }
          const m = methodsRef.current;
          if (m && prop in m) {
            if (!participantRef.current) {
              return (...args) => {
                console.warn(
                  `useParticipant: ${String(
                    prop
                  )}() called but participant "${participantId}" not found.`
                );
              };
            }
            return m[prop];
          }
          return Reflect.get(target, prop, receiver);
        },
        has(target, prop) {
          return (
            prop in PARTICIPANT_STATE_KEYS ||
            !!(methodsRef.current && prop in methodsRef.current)
          );
        },
        ownKeys() {
          const stateKeys = Object.keys(PARTICIPANT_STATE_KEYS);
          const mKeys = Object.keys(methodsRef.current ?? {});
          return [...new Set([...stateKeys, ...mKeys])];
        },
        getOwnPropertyDescriptor() {
          return { configurable: true, enumerable: true, writable: false };
        }
      }),
    [allState, participantId]
  );
};

export default useParticipant;
